import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableRow, Checkbox, Box } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import {
  PERMISSION_MODULES,
  PERMISSION_OPERATIONS,
  initializeModulePermissions,
  hasAnyPermission,
  hasAllPermissions,
} from "utils/permissionConstants";

/**
 * Reusable Permission Table Component
 * @param {Object} selectedPermissions - Current selected permissions object
 * @param {Function} onPermissionChange - Callback when permission changes
 * @param {Boolean} selectAllModules - Whether all modules are selected
 * @param {Function} onSelectAllModules - Callback for select all modules
 */
function PermissionTable({
  selectedPermissions,
  onPermissionChange,
  selectAllModules,
  onSelectAllModules,
}) {
  // Handle individual operation change
  const handleOperationChange = (moduleKey, operation) => {
    onPermissionChange({
      ...selectedPermissions,
      [moduleKey]: {
        ...initializeModulePermissions(moduleKey, selectedPermissions),
        [operation]: !selectedPermissions[moduleKey]?.[operation],
      },
    });
  };

  // Handle module "Select All" (all CRUD operations)
  const handleModuleSelectAll = (moduleKey) => {
    const currentPerms = initializeModulePermissions(moduleKey, selectedPermissions);
    const allSelected = Object.values(currentPerms).every((val) => val === true);

    onPermissionChange({
      ...selectedPermissions,
      [moduleKey]: {
        create: !allSelected,
        get: !allSelected,
        update: !allSelected,
        delete: !allSelected,
      },
    });
  };

  return (
    <ArgonBox
      mt={4}
      sx={{
        borderRadius: 3,
        background: "linear-gradient(145deg, #ffffff, #f9f9ff)",
        p: 3,
        boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
      }}
    >
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h6" fontWeight="bold">
          Permissions (Granular Control)
        </ArgonTypography>
        <ArgonButton
          variant={selectAllModules ? "contained" : "outlined"}
          color="primary"
          size="small"
          onClick={onSelectAllModules}
          sx={{ borderRadius: "8px" }}
        >
          {selectAllModules ? "Deselect All" : "Select All Modules"}
        </ArgonButton>
      </ArgonBox>

      <ArgonTypography variant="caption" color="text" mb={2} display="block">
        Select specific operations (Create, Get, Update, Delete) for each module
      </ArgonTypography>

      <Box sx={{ overflowX: "auto" }}>
        <Table>
          <TableBody>
            {/* Header Row */}
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell sx={{ fontWeight: "bold", width: "25%", minWidth: "150px" }}>
                Module
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", minWidth: "80px" }}>
                Create
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", minWidth: "80px" }}>
                Get
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", minWidth: "80px" }}>
                Update
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", minWidth: "80px" }}>
                Delete
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", minWidth: "80px" }}>
                All
              </TableCell>
            </TableRow>

            {/* Permission Rows */}
            {PERMISSION_MODULES.map((module, idx) => {
              const modulePerms = initializeModulePermissions(module.key, selectedPermissions);
              const allSelected = hasAllPermissions(selectedPermissions, module.key);
              const anySelected = hasAnyPermission(selectedPermissions, module.key);

              return (
                <TableRow
                  key={module.key}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? "#fafbff" : "white",
                    "&:hover": { backgroundColor: "#f1f5ff" },
                  }}
                >
                  <TableCell>
                    <ArgonTypography variant="button" fontWeight="medium">
                      {module.label}
                    </ArgonTypography>
                  </TableCell>

                  {/* Create */}
                  <TableCell align="center">
                    <Checkbox
                      checked={modulePerms.create || false}
                      onChange={() => handleOperationChange(module.key, "create")}
                      sx={{
                        color: "#4caf50",
                        "&.Mui-checked": { color: "#4caf50" },
                      }}
                    />
                  </TableCell>

                  {/* Get */}
                  <TableCell align="center">
                    <Checkbox
                      checked={modulePerms.get || false}
                      onChange={() => handleOperationChange(module.key, "get")}
                      sx={{
                        color: "#2196f3",
                        "&.Mui-checked": { color: "#2196f3" },
                      }}
                    />
                  </TableCell>

                  {/* Update */}
                  <TableCell align="center">
                    <Checkbox
                      checked={modulePerms.update || false}
                      onChange={() => handleOperationChange(module.key, "update")}
                      sx={{
                        color: "#ff9800",
                        "&.Mui-checked": { color: "#ff9800" },
                      }}
                    />
                  </TableCell>

                  {/* Delete */}
                  <TableCell align="center">
                    <Checkbox
                      checked={modulePerms.delete || false}
                      onChange={() => handleOperationChange(module.key, "delete")}
                      sx={{
                        color: "#f44336",
                        "&.Mui-checked": { color: "#f44336" },
                      }}
                    />
                  </TableCell>

                  {/* Select All for Module */}
                  <TableCell align="center">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={anySelected && !allSelected}
                      onChange={() => handleModuleSelectAll(module.key)}
                      sx={{
                        color: "#9c27b0",
                        "&.Mui-checked": { color: "#9c27b0" },
                        "&.MuiCheckbox-indeterminate": { color: "#9c27b0" },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Legend */}
      <ArgonBox mt={2} display="flex" gap={3} flexWrap="wrap" justifyContent="center">
        {PERMISSION_OPERATIONS.map((op) => (
          <ArgonBox key={op.key} display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: op.color,
                borderRadius: "50%",
              }}
            />
            <ArgonTypography variant="caption">{op.label}</ArgonTypography>
          </ArgonBox>
        ))}
        <ArgonBox display="flex" alignItems="center" gap={0.5}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: "#9c27b0",
              borderRadius: "50%",
            }}
          />
          <ArgonTypography variant="caption">All Operations</ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    </ArgonBox>
  );
}

// PropTypes validation
PermissionTable.propTypes = {
  selectedPermissions: PropTypes.object.isRequired,
  onPermissionChange: PropTypes.func.isRequired,
  selectAllModules: PropTypes.bool.isRequired,
  onSelectAllModules: PropTypes.func.isRequired,
};

export default PermissionTable;
